/**
 * What a marker's SHAPE says.
 *
 * Every marker was a circle, whatever it was - a Rhino, a swarm and a
 * Space Marine drew identically, so the map could tell you where
 * somebody was and never what they were.
 *
 * THE SHAPE IS THE ONE CHANNEL STILL FREE. The footprint's stroke
 * carries the ROLE (blue you, red your target), the ring around it
 * carries the WOUNDS, the wedge outside that carries the AMMO, and the
 * space inside was saying nothing at all. So the shape says what kind of
 * thing this is, and none of the other three change: the status ring is
 * an ellipse drawn AROUND the footprint at a fixed gap, not the
 * footprint itself, so a square vehicle sits inside exactly the same
 * gauge as everybody else. A fraction is only readable as an arc.
 *
 * Humanoid keeps the ellipse and the circle - as elements, not paths.
 * The `.tokenPickable` pointer-events whitelist is a hardcoded class
 * list, specs read the footprint's `cx`, and the commonest case has
 * nothing to gain from being redrawn. `null` means "keep the primitive".
 */
import type { BodyShape } from '../bodyShape';
import { resolveBodyShape, isCrowd } from '../bodyShape';

/**
 * THE SHAPE RULE LIVES IN THE MODEL, not here. The map asks what kind of
 * thing a target is; it does not get to decide. `engine/bodyShape` is
 * the one answer, shared with the target editor so the picture and the
 * sheet cannot disagree (v8.44.0).
 */
export type ShapeKind = BodyShape;
export { resolveBodyShape as resolveBodyType, isCrowd };

/** `M x y L … Z` from a list of points. */
function closedPath(points: [number, number][]): string {
  const [first, ...rest] = points;
  const at = ([x, y]: [number, number]) => `${round(x)} ${round(y)}`;
  return `M ${at(first)} ${rest.map((p) => `L ${at(p)}`).join(' ')} Z`;
}

/** Two decimals is a tenth of a pixel - below that is noise in the DOM. */
function round(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * The footprint on the ground, as a path - or `null` to keep the ellipse.
 *
 * `rx`/`ry` are the ellipse's own radii, already foreshortened by the
 * camera pitch, so every shape here is drawn inside the same oval and
 * the ring outside it never has to move.
 */
export function footShape(kind: ShapeKind, cx: number, cy: number, rx: number, ry: number): string | null {
  if (kind === 'vehicle') {
    // A hull: a rectangle inscribed in the same oval.
    return closedPath([
      [cx - rx, cy - ry],
      [cx + rx, cy - ry],
      [cx + rx, cy + ry],
      [cx - rx, cy + ry],
    ]);
  }
  if (kind === 'amorphous') {
    // A diamond - the same extent, and unmistakably neither of the
    // other two at a glance.
    return closedPath([
      [cx, cy - ry],
      [cx + rx, cy],
      [cx, cy + ry],
      [cx - rx, cy],
    ]);
  }
  // An Object (2026-10-26): a hexagon - the diamond is the amorphous mass's and the square a hull's.
  if (kind === 'object') return closedPath(hexagon(cx, cy, rx, ry));
  return null;
}

/** Six corners round (cx, cy) inside an rx by ry oval, flat top and bottom. */
function hexagon(cx: number, cy: number, rx: number, ry: number): [number, number][] {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (i * Math.PI) / 3;
    return [cx + rx * Math.cos(a), cy + ry * Math.sin(a)];
  });
}

/**
 * The bodies that make a horde read as a CROWD.
 *
 * A horde already draws its ground area and prints its magnitude - how
 * much room it takes and how many are left. Neither says "mob" at a
 * glance, which is what you want before reading anything.
 *
 * THREE OF EQUAL SIZE, NOT ONE WITH SATELLITES. The first attempt kept
 * a full-size body and hung smaller discs off it at 0.55 of its radius -
 * so they sat mostly inside it and poked out. It read as "a circle with
 * ears", which is precisely what it was (v8.43.0).
 *
 * These are all `scale` of the ordinary body and sit `0.58` away from
 * the centre, so neighbouring discs are about 1.0 apart against a
 * combined width of 1.04 - they touch and barely overlap, which is what
 * makes three shapes read as three bodies rather than one lumpy one. The
 * whole cluster spans 1.02 of the ordinary body, so nothing reaches the
 * wound ring.
 *
 * Offsets are in body-radius units; the caller scales them. Deliberately
 * NOT proportional to magnitude - the area ring already carries that,
 * and forty little circles is a smear, not a crowd.
 */
export const CROWD: readonly { dx: number; dy: number; scale: number }[] = [
  { dx: 0, dy: -0.58, scale: 0.52 },
  { dx: -0.502, dy: 0.29, scale: 0.52 },
  { dx: 0.502, dy: 0.29, scale: 0.52 },
];

/**
 * The body on its stalk, as a path - or `null` to keep the circle.
 *
 * SHAPED AT BOTH ENDS. Looking straight down the stalk vanishes and the
 * body sits on the footprint; from a low angle the footprint is a sliver
 * and the body is nearly all you see. Shaping only one of them would
 * leave the map type-blind at one camera or the other.
 */
export function bodyShape(kind: ShapeKind, cx: number, cy: number, r: number): string | null {
  if (kind === 'vehicle') {
    return closedPath([
      [cx - r, cy - r],
      [cx + r, cy - r],
      [cx + r, cy + r],
      [cx - r, cy + r],
    ]);
  }
  if (kind === 'amorphous') {
    return closedPath([
      [cx, cy - r],
      [cx + r, cy],
      [cx, cy + r],
      [cx - r, cy],
    ]);
  }
  if (kind === 'object') return closedPath(hexagon(cx, cy, r, r));
  return null;
}
